import React, {ReactNode, useCallback, useEffect, useMemo, useState} from 'react'
import {useSelector} from "react-redux"
import {getPermission} from "redux/appEnvironment.selector"
import {getAcesListSelector, getAssocClients} from "redux/users.selector"
import UserForm from "./UserForm"
import {Button  , TableColumnProps, Tooltip} from 'antd'
import styled from "styled-components"
import {
  IconContainer,
  AdministrationLayout,
  ContentCard,
  BironTable, Confirm
} from "@biron-data/react-components"
import Language from "language"
import NewUacForm from "./NewUacForm"
import useDispatch from "hooks/useDispatch"
import {
  UserAddIcon,
  BanIcon, TrashIcon,
} from "@heroicons/react/outline"
import {useNavigate} from "react-router-dom"
import {DataSourceEntry, GetComponentProps, UAC, User} from "components/admin/user/UserManager.types"
import {PermissionsEnum} from "redux/models/appEnvironment"
import {filterData, Search} from "../../search/Search";
import UserManagerInfo from "./UserManager.Info";
import {getUserPosition} from "./UserManager.utils";
import useUACWithAces, {UserPosition} from "../../../hooks/useUACWithAces";
import DatamodelRights from "./DatamodelRights";
import WorkspaceRights from "./WorkspaceRights";
import {Restore} from "@biron-data/icons/dist/outlined"
import {useGetDatamodelRolesSynthesis, useGetRolesSynthesis} from "./UserDetails.hooks";
import AdminBreadcrumb from "../Admin.Breadcrumb";
import {buildWorkspaceAdminUsersUri, buildWorkspaceAdminUserUri} from "../../../commons/buildWorkspaceAdminUri";
import {getCurrentWorkspace} from "../../../redux/workspace.selector";
import {TooltipPlacement} from "antd/es/tooltip";
import {useGetIsCurrentUser, useHandleActiveChange} from "./UserManager.hooks";
import {useGetPermUserCanEdit} from "../../../hooks/usePermUserCanEdit";
import {useHandleResetPassword} from "../../../hooks/useHandleResetPassword";

const UserManager = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const uacs: UAC[] = useSelector(getAssocClients)
  const workspace = useSelector(getCurrentWorkspace)
  const acesByUserId = useSelector(getAcesListSelector)
  const getPermissionSelector = useSelector(getPermission)
  const [createNewUac, setCreateNewUac] = useState(false)
  const [editedUac, setEditedUac] = useState<UAC>()
  const [loading, setLoading] = useState(true)
  const userCanAssocToEnvironment = getPermissionSelector(PermissionsEnum.userCanAssocToEnvironment)

  const getIsCurrentUser = useGetIsCurrentUser()

  const loadData = useCallback(() => {
    dispatch.users.loadAssocClients({forBatch: true}).then(() => {
      dispatch.users.loadAcesByEnvironment({forBatch: true}).then(() => {
        setLoading(false)
      })
    })
  }, [dispatch.users])

  useEffect(() => {
    loadData()
  }, [loadData])

  const handleClickNewUser = useCallback(() => {
    setEditedUac(Object.assign({} as UAC, {user: {}}))
  }, [])
  const handleClickNewUac = useCallback(() => {
    setCreateNewUac(true)
  }, [])

  const handleActiveChange = useHandleActiveChange(dispatch.users.updateAssocClientActive)

  const handleResetPassword = useHandleResetPassword(
    setEditedUac,
    dispatch.users.resetPassword
  )

  const handleUserFormConfirm = useCallback(
    (data: User) => {
      return editedUac?.user.id
          ? dispatch.users.updateUser({
            id: editedUac.user.id,
            data,
          }).then(() => {
          setEditedUac(undefined)
        })
          : dispatch.users.createUser({data}).then((uac) => {
            setEditedUac(undefined)
            if (workspace) {
              navigate(buildWorkspaceAdminUserUri(workspace, `${uac.id}`))
            }
          })
    }
    , [dispatch.users, editedUac?.user.id, navigate, workspace])
  const handleUserFormCancel = useCallback(() => {
    setEditedUac(undefined)
  }, [])

  const handleNewUacFormConfirm = useCallback(
    ({userEmail}: { userEmail: string }) => dispatch.users.createAssocClient({userEmail})
      .then((uac) => {
        setCreateNewUac(false)
        if (workspace) {
          navigate(buildWorkspaceAdminUserUri(workspace, `${uac.user.id}`))
        }
      })
    , [dispatch.users, navigate, workspace])

  const handleNewUacFormCancel = useCallback(() => {
    setCreateNewUac(false)
  }, [])

  const addUserActions = useMemo(() => {
    const basicAdd = <FlexButton type={"primary"} key="actionCreateUser"
                                 onClick={handleClickNewUser}><IconContainer
      margin={10}><UserAddIcon/></IconContainer>{Language.get('admin.user.actions.createUser')}</FlexButton>
    if (userCanAssocToEnvironment) {
      return [basicAdd,
        <FlexButton type={"primary"} key="actionCreateUac"
                    onClick={handleClickNewUac}><IconContainer
          margin={10}><UserAddIcon/></IconContainer>{Language.get('admin.user.actions.createUac')}</FlexButton>,
      ]
    } else {
      return [basicAdd]
    }
  }, [userCanAssocToEnvironment, handleClickNewUser, handleClickNewUac])

  const [search, setSearch] = useState("")

  const uacWithAces = useUACWithAces(uacs, acesByUserId)

  const getPermUserCanEdit = useGetPermUserCanEdit()

  const data: DataSourceEntry[] = useMemo(() => uacWithAces.map(e => ({
    ...e,
    permUserCanEdit: getPermUserCanEdit(e.user),
  })), [uacWithAces, getPermUserCanEdit])

  const [dataSource, setDataSource] = useState<DataSourceEntry[]>(data)

  useEffect(() => {
    setDataSource(filterData(data, (record) => [
      record.user.email,
      record.user.firstName,
      record.user.fullName,
      record.user.lastName,
    ], search))
  }, [data, search])

  const handleDelete = useCallback((uac: { id: string | number }) => {
    setEditedUac(undefined)
    dispatch.users.deleteAssocClient({
      forBatch: true,
      uac,
    })
  }, [dispatch.users])

  const getRolesSynthesis = useGetRolesSynthesis()
  const getDatamodelRolesSynthesis = useGetDatamodelRolesSynthesis()

  const getRightRepresentation = useCallback((
    uac: DataSourceEntry,
    layout: 'row' | 'column',
    tooltipPlacement?: TooltipPlacement,
    popover?: ReactNode
  ) => {
   return <>
     <DatamodelRights
       roles={getDatamodelRolesSynthesis(uac.user.aces)}
       layout={layout} tooltipPlacement={tooltipPlacement}
       popover={popover}/>
     {layout === "row" && <div style={{border: "1px solid var(--light-grey)"}}></div>}
     <WorkspaceRights
      roles={getRolesSynthesis(uac.user.aces)}
      layout={layout} tooltipPlacement={tooltipPlacement}
      popover={popover}/>
     </>
  }, [getDatamodelRolesSynthesis, getRolesSynthesis])

  const columns: TableColumnProps<DataSourceEntry>[] = useMemo(() => ([
    {
      title: Language.get('admin.user.columns.user'),
      dataIndex: ['user', 'firstName'],
      width: "40%",
      render: function render(_, uac) {
        return <UserManagerInfo user={uac.user} displayEmail={true}
                                onUserNameClick={() => {
                                  if (workspace) {
                                    navigate(buildWorkspaceAdminUserUri(workspace, `${uac.user.id}`))
                                  }
                                }}/>
      },
    },
    {
      title: Language.get("admin.user.columns.position"),
      dataIndex: ['user', 'position'],
      className: 'verticalAlignTop',
      width: "20%",
      filters: Object.keys(UserPosition).map((position) => ({
        text: Language.get(`admin.user.position.${position}`),
        value: position,
      })),
      onFilter: (value, record) => record.user.position === value.toString(),
      render: (position) =>
        getUserPosition(position),
    },
    {
      title: Language.get("admin.user.columns.rights"),
      width: "30%",
      className: 'verticalAlignTop',
      render: (_, uac) => <FlexDiv
        $direction={"row"}
        $isClickable={true}
        onClick={() => {
        if (workspace) {
          navigate(buildWorkspaceAdminUserUri(workspace, `${uac.user.id}`))
        }
      }}>
        {getRightRepresentation(uac, "row", "left", getRightRepresentation(uac, "column"))}
      </FlexDiv>,
    },
    {
      title: <FlexEndDiv $direction={"row"}>{Language.get("admin.user.columns.actions")}</FlexEndDiv>,
      width: "10%",
      className: 'verticalAlignTop',
      render: function render(_, uac) {
        if (!uac.permUserCanEdit) {
          return <></>
        }
        if (!uac.active) {
          return <FlexEndDiv $direction={"row"}>
            <Tooltip title={Language.get("admin.user.tooltips.activate")}>
              <div>
              <IconContainer
                color={"var(--primary)"}
                clickable={true}
                onClick={() => {
                  handleActiveChange(uac, true)
                }}><Restore/></IconContainer>
              </div>
            </Tooltip>
            <Confirm
              title={Language.get('admin.user.confirm.title', uac.user.fullName)}
              content={<FlexDiv $direction={"column"}>
                <span>{Language.get('admin.user.confirm.email', uac.user.email)}</span>
                <span>{Language.get('admin.user.confirm.content')}</span>
              </FlexDiv>}
              trigger={<Tooltip title={Language.get("admin.user.tooltips.delete")}>
                <div>
                  <IconContainer color={"red"} clickable={true}><TrashIcon/></IconContainer>
                </div>
              </Tooltip>}
              on={{
                cancel: () => {
                  setEditedUac(undefined)
                },
                open: () => {},
                ok: async () => {
                  handleDelete(uac)
                },
              }}/>
          </FlexEndDiv>
        }
        return getIsCurrentUser(uac.user) ? <></> : <FlexEndDiv $direction={"row"}>
          <Tooltip title={Language.get("admin.user.tooltips.inactivate")}>
            <div>
              <IconContainer color={"red"} clickable={true} onClick={() => {
                handleActiveChange(uac, false)
              }}><BanIcon /></IconContainer>
            </div>
          </Tooltip>
        </FlexEndDiv>
      },
    },
  ]), [getIsCurrentUser, getRightRepresentation, handleActiveChange, handleDelete, navigate, workspace])

  const onRow: GetComponentProps<DataSourceEntry> = useCallback((record) => {
    return {
      onClick: () => {
        if (workspace) {
          navigate(buildWorkspaceAdminUserUri(workspace, `${record.user.id}`))
        }
      }
    }
  }, [navigate, workspace])

  return <AdministrationLayout breadcrumb={<AdminBreadcrumb
  category={{
    title: Language.get('admin.user.breadcrumb.title'),
    link: workspace ? buildWorkspaceAdminUsersUri(workspace) : undefined
  }}/>}>
    <ContentCard title={Language.get('admin.user.content.title')} actions={[
      addUserActions
    ]}>
        <Search onChange={(newValues) => setSearch(newValues)} width={'calc(100% - 25px)'} searchedFieldKey={"admin.user.columns.user"}/>
        <BironTable {...{
          size: 'small',
          bordered: false,
          rowHeight: 76,
          columns,
          loading,
          dataSource,
          onRow,
          rowKey: 'id',
          minWidth: 800,
        }}/>

    </ContentCard>
    <UserForm {...{
      uac: editedUac,
      onActiveChange: handleActiveChange,
      onResetPassword: handleResetPassword,
      onDelete: handleDelete,
      onConfirm: handleUserFormConfirm,
      onCancel: handleUserFormCancel,
    }}/>
    <NewUacForm {...{
      visible: createNewUac,
      onConfirm: handleNewUacFormConfirm,
      onCancel: handleNewUacFormCancel,
    }}/>
  </AdministrationLayout>
}

export default UserManager

const FlexButton = styled(Button)`
  display: flex;
`

const FlexDiv = styled.div<{
  $direction: "column" | "row",
  $isClickable?: boolean
}>`
    display: flex;
    justify-content: start;
    ${({$isClickable}) => $isClickable ? "cursor: pointer;" : ""}
    flex-direction: ${({$direction}) => $direction};
    gap: 10px;
`

const FlexEndDiv = styled(FlexDiv)`
    justify-content: end;
`